(() => {
class ToyamaLp extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setupHeroIntro();
    this.setupHeroDecorationShuffle();
    this.setupRotator('[data-hero-slide]', '[data-hero-dot]', Number(this.dataset.heroInterval) || 5000);
    this.setupRotator('[data-article-banner-slide]', null, Number(this.dataset.articleBannerInterval) || 6000);
    this.setupScrollReveal();
    this.setupMountainDraw();
    this.setupScrollMotion();
    this.setupMapPlay();
    this.setupMakersDecoration();
    this.querySelectorAll('[data-carousel]').forEach((carousel) => {
      this.setupCarousel(carousel);
    });
    this.querySelectorAll('[data-map-close]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    this.addEventListener('click', (event) => {
      const mapButton = event.target.closest('[data-map-marker]');
      if (mapButton && this.contains(mapButton)) {
        const dialog = this.querySelector(`#${mapButton.dataset.mapDialog}`);
        this.openDialog(dialog);
        return;
      }
      const button = event.target.closest('[data-dialog-trigger]');
      if (!button || !this.contains(button)) return;
      const dialog = this.querySelector(`#${button.dataset.dialogTrigger}`);
      this.openDialog(dialog);
    });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => this.unlockPageScroll());
    });
  }

  async setupHeroIntro() {
    if (this.reducedMotion) return;
    this.classList.add('is-hero-loading');
    const heroImage = this.querySelector('.toyama-lp__hero-slide.is-active img');
    const imageReady = heroImage?.decode ? heroImage.decode().catch(() => {}) : Promise.resolve();
    const fontsReady = document.fonts?.ready || Promise.resolve();
    await Promise.all([imageReady, fontsReady]);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.classList.add('is-hero-ready'));
    });
  }

  setupHeroDecorationShuffle() {
    this.heroDecorations = [...this.querySelectorAll('.toyama-lp__hero-decoration')];
    const configuredSources = (this.dataset.heroDecorationAssets || '').split('|').filter(Boolean);
    this.heroDecorationSources = configuredSources.length
      ? configuredSources
      : this.heroDecorations.map((decoration) => decoration.querySelector('img')?.src).filter(Boolean);
    this.heroDecorationSources.forEach((source) => {
      const image = new Image();
      image.src = source;
    });
    this.heroDecorationReady = false;
  }

  shuffleHeroDecorations() {
    if (this.reducedMotion || this.heroDecorations?.length < 2 || this.heroDecorationSources?.length < 2) return;
    if (!this.heroDecorationReady) {
      this.heroDecorationReady = true;
      return;
    }

    const currentSources = this.heroDecorations.map((decoration) => decoration.querySelector('img')?.src);
    let nextSources;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      nextSources = [...this.heroDecorationSources];
      for (let index = nextSources.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [nextSources[index], nextSources[swapIndex]] = [nextSources[swapIndex], nextSources[index]];
      }
      nextSources = nextSources.slice(0, this.heroDecorations.length);
      if (nextSources.every((source, index) => source !== currentSources[index])) break;
    }

    this.heroDecorations.forEach((decoration, index) => {
      decoration.style.setProperty('--hero-decor-turn', `${Math.random() > 0.5 ? 12 : -12}deg`);
      decoration.classList.add('is-swapping');
      window.setTimeout(() => {
        const image = decoration.querySelector('img');
        if (image && nextSources[index]) image.src = nextSources[index];
        decoration.classList.remove('is-swapping');
      }, 450);
    });
  }

  setupMountainDraw() {
    const mountain = this.querySelector('.toyama-lp__intro-mountain');
    const trigger = this.querySelector('.toyama-lp__intro-copy strong, .toyama-lp__intro-copy');
    if (!mountain || !trigger) return;
    mountain.classList.add('toyama-lp__intro-mountain-draw');
    if (this.reducedMotion) {
      mountain.classList.add('is-drawn');
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      mountain.classList.add('is-drawn');
      observer.disconnect();
    }, { threshold: 0.15, rootMargin: '0px 0px -12% 0px' });
    observer.observe(trigger);
  }

  setupScrollMotion() {
    if (this.reducedMotion) return;
    const movingElements = [
      ...this.querySelectorAll('.toyama-lp__hero-decoration'),
      this.querySelector('.toyama-lp__intro-mountain'),
    ].filter(Boolean);
    if (!movingElements.length) return;

    let frame;
    const update = () => {
      frame = null;
      const viewportCenter = window.innerHeight * 0.5;
      movingElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const distance = (rect.top + rect.height * 0.5 - viewportCenter) / window.innerHeight;
        const amount = Math.max(-1, Math.min(1, distance));
        element.style.setProperty('--scroll-offset', `${(amount * -18).toFixed(2)}px`);
      });
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();
  }

  setupScrollReveal() {
    const targets = this.querySelectorAll('.toyama-lp__intro, .toyama-lp__map, .toyama-lp__map-legend, .toyama-lp__makers, .toyama-lp__article-banner, .toyama-lp__article-group, .toyama-lp__article-card, .toyama-lp__map-marker, .toyama-lp__maker');
    if (!targets.length) return;
    targets.forEach((target) => {
      if (target.matches('.toyama-lp__intro')) {
        target.classList.add('toyama-lp__intro-reveal');
        return;
      }
      if (target.matches('.toyama-lp__makers')) {
        target.classList.add('toyama-lp__makers-reveal');
        return;
      }
      if (target.matches('.toyama-lp__article-banner')) {
        target.classList.add('toyama-lp__article-banner-motion');
        return;
      }
      if (target.matches('.toyama-lp__map-legend')) {
        target.classList.add('toyama-lp__map-legend-reveal');
        return;
      }
      if (!target.matches('.toyama-lp__map-marker, .toyama-lp__maker, .toyama-lp__article-card')) {
        target.classList.add('toyama-lp__scroll-reveal');
      }
    });
    this.querySelectorAll('.toyama-lp__map-marker').forEach((target, index) => {
      const entries = [
        ['-46px', '-28px', '-18deg'], ['34px', '-42px', '14deg'],
        ['52px', '12px', '20deg'], ['-38px', '34px', '-14deg'],
      ];
      const [entryX, entryY, entryRotate] = entries[index % entries.length];
      target.classList.add('toyama-lp__reveal-pop');
      target.style.setProperty('--marker-reveal-delay', `${550 + index * 65}ms`);
      target.style.setProperty('--marker-entry-x', entryX);
      target.style.setProperty('--marker-entry-y', entryY);
      target.style.setProperty('--marker-entry-rotate', entryRotate);
    });
    this.querySelectorAll('.toyama-lp__maker').forEach((target) => {
      target.classList.add('toyama-lp__reveal-side');
      target.style.setProperty('--reveal-delay', '80ms');
      target.style.setProperty('--maker-reveal-delay', '80ms');
    });
    this.querySelectorAll('.toyama-lp__article-group').forEach((group) => {
      group.querySelectorAll('.toyama-lp__article-card').forEach((target, index) => {
        target.classList.add('toyama-lp__reveal-card');
        target.style.setProperty('--reveal-delay', `${index * 110}ms`);
      });
    });
    if (this.reducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    targets.forEach((target) => observer.observe(target));
  }

  setupMapPlay() {
    const map = this.querySelector('.toyama-lp__map');
    if (!map || this.reducedMotion) return;
    let frameId;
    const update = () => {
      frameId = null;
      const rect = map.getBoundingClientRect();
      const viewportCenter = window.innerHeight * 0.5;
      const mapCenter = rect.top + rect.height * 0.5;
      const drift = Math.max(-1, Math.min(1, (mapCenter - viewportCenter) / window.innerHeight));
      map.style.setProperty('--map-drift', `${(drift * -12).toFixed(2)}px`);
    };
    const requestUpdate = () => {
      if (!frameId) frameId = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();
  }

  setupMakersDecoration() {
    const makers = this.querySelector('.toyama-lp__makers');
    if (!makers) return;
    if (this.reducedMotion || !('IntersectionObserver' in window)) {
      makers.classList.add('is-decoration-visible');
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      makers.classList.add('is-decoration-visible');
      observer.disconnect();
    }, { threshold: 0, rootMargin: '0px 0px 28% 0px' });
    observer.observe(makers);
  }

  openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('toyama-lp-modal-open');
  }

  unlockPageScroll() {
    if (document.querySelector('.toyama-lp__map-dialog[open]')) return;
    document.documentElement.classList.remove('toyama-lp-modal-open');
  }

  setupCarousel(carousel) {
    if (carousel.dataset.carouselReady === 'true') return;
    carousel.dataset.carouselReady = 'true';

    const track = carousel.querySelector('[data-track]');
    if (!track) return;

    carousel.querySelector('[data-prev]')?.addEventListener('click', () => this.scrollCarousel(carousel, -1));
    carousel.querySelector('[data-next]')?.addEventListener('click', () => this.scrollCarousel(carousel, 1));

    const cards = [...track.querySelectorAll('.toyama-lp__article-card, .toyama-lp__product-card')];
    track.classList.toggle('has-overflow-preview', cards.length > 3);
  }

  setupRotator(slideSelector, pagerSelector, interval) {
    const slides = [...this.querySelectorAll(slideSelector)];
    if (slides.length < 2) return;
    const pagers = pagerSelector ? [...this.querySelectorAll(pagerSelector)] : [];
    let active = 0;
    let timer;
    const show = (index) => {
      active = (index + slides.length) % slides.length;
      const previous = (active - 1 + slides.length) % slides.length;
      const next = (active + 1) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === active);
        slide.classList.toggle('is-prev', slides.length > 2 && i === previous);
        slide.classList.toggle('is-next', i === next && i !== active);
      });
      pagers.forEach((pager, i) => {
        pager.classList.toggle('is-active', i === active);
        pager.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
      if (slideSelector === '[data-hero-slide]') this.shuffleHeroDecorations();
    };
    const start = () => {
      if (this.reducedMotion) return;
      window.clearInterval(timer);
      timer = window.setInterval(() => show(active + 1), interval);
    };
    pagers.forEach((pager, index) => pager.addEventListener('click', () => { show(index); start(); }));
    show(0);
    start();
  }

  scrollCarousel(carousel, direction) {
    const track = carousel.querySelector('[data-track]');
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(track.clientWidth * .8, 260), behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('toyama-lp')) customElements.define('toyama-lp', ToyamaLp);
})();
